import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Save, Plus, Trash2 } from "lucide-react";

const DAYS = [
  { key: "mon", label: "Montag" },
  { key: "tue", label: "Dienstag" },
  { key: "wed", label: "Mittwoch" },
  { key: "thu", label: "Donnerstag" },
  { key: "fri", label: "Freitag" },
  { key: "sat", label: "Samstag" },
  { key: "sun", label: "Sonntag" },
];

export default function AvailabilityManager() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newExDate, setNewExDate] = useState("");

  useEffect(() => {
    api.get("/cms/availability").then(({ data }) => setData({ weekly: data.weekly || {}, exceptions: data.exceptions || {} }));
  }, []);
  if (!data) return <div className="p-8">Lade...</div>;

  const setWeekly = (day, idx, field, value) => {
    const windows = [...(data.weekly[day] || [])];
    windows[idx] = { ...windows[idx], [field]: value };
    setData({ ...data, weekly: { ...data.weekly, [day]: windows } });
  };
  const addWindow = (day) => {
    const windows = [...(data.weekly[day] || []), { start: "09:00", end: "18:00" }];
    setData({ ...data, weekly: { ...data.weekly, [day]: windows } });
  };
  const removeWindow = (day, idx) => {
    const windows = (data.weekly[day] || []).filter((_, i) => i !== idx);
    setData({ ...data, weekly: { ...data.weekly, [day]: windows } });
  };
  const toggleExceptionClosed = (date) => {
    const ex = { ...data.exceptions };
    if (ex[date]?.closed) delete ex[date]; else ex[date] = { closed: true };
    setData({ ...data, exceptions: ex });
  };
  const addException = () => {
    if (!newExDate) return;
    const ex = { ...data.exceptions, [newExDate]: { closed: true } };
    setData({ ...data, exceptions: ex });
    setNewExDate("");
  };
  const removeException = (date) => {
    const ex = { ...data.exceptions }; delete ex[date];
    setData({ ...data, exceptions: ex });
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/cms/availability", { weekly: data.weekly, exceptions: data.exceptions }); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 lg:p-12 mv-fade-in max-w-4xl">
      <div className="flex items-end justify-between mb-10">
        <div><p className="mv-label mb-2">Buchungs-Kalender</p><h1 className="font-display text-4xl">Verfügbarkeit</h1></div>
        <button data-testid="save-availability-btn" onClick={save} disabled={saving} className="mv-btn"><Save className="w-4 h-4" /> {saving ? "..." : "Speichern"}</button>
      </div>

      <div className="mv-card p-6 mb-6">
        <p className="mv-label mb-4">Wochenöffnungszeiten</p>
        <div className="space-y-3">
          {DAYS.map((d) => {
            const windows = data.weekly[d.key] || [];
            return (
              <div key={d.key} className="grid md:grid-cols-[120px_1fr_auto] gap-3 items-start" data-testid={`day-${d.key}`}>
                <p className="text-sm font-medium pt-2">{d.label}</p>
                <div className="space-y-2">
                  {windows.length === 0 && <p className="text-xs italic" style={{ color: "var(--mv-text-muted)" }}>Geschlossen</p>}
                  {windows.map((w, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input type="time" className="mv-input text-sm w-28" value={w.start} onChange={(e) => setWeekly(d.key, i, "start", e.target.value)} />
                      <span className="text-xs opacity-60">bis</span>
                      <input type="time" className="mv-input text-sm w-28" value={w.end} onChange={(e) => setWeekly(d.key, i, "end", e.target.value)} />
                      <button onClick={() => removeWindow(d.key, i)} className="p-1.5 opacity-60 hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => addWindow(d.key)} className="mv-btn-ghost text-xs"><Plus className="w-3 h-3" /> Zeitfenster</button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mv-card p-6">
        <p className="mv-label mb-3">Ausnahmen (geschlossene Tage)</p>
        <p className="text-xs mb-4" style={{ color: "var(--mv-text-muted)" }}>Feiertage, Urlaub oder einzelne Schließtage</p>
        <div className="flex gap-2 mb-4">
          <input type="date" className="mv-input text-sm" value={newExDate} onChange={(e) => setNewExDate(e.target.value)} />
          <button data-testid="add-exception-btn" onClick={addException} className="mv-btn-ghost text-sm"><Plus className="w-3 h-3" /> Hinzufügen</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.exceptions).map(([date, ex]) => (
            <div key={date} className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm" style={{ borderColor: "var(--mv-border)" }}>
              <span>{date}</span>
              <span className="text-xs opacity-60">{ex.closed ? "geschlossen" : "Sonderzeit"}</span>
              <button onClick={() => removeException(date)} className="opacity-60 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {Object.keys(data.exceptions).length === 0 && <p className="text-xs italic" style={{ color: "var(--mv-text-muted)" }}>Keine Ausnahmen definiert.</p>}
        </div>
      </div>
    </div>
  );
}
